import { Component, Input, NgZone, OnChanges, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import { Chart, ChartConfiguration, ChartData, Plugin } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { TempDataPoint, SolarHistoryPoint } from '../version.service';
import { DeepLinkService } from '../shared/deep-link.service';
import { chartPalette, timeTooltipTitle, lineLegendLabels, dragZoom, relativeDayTick, timeAxisTick, setpointColor, alignedTimeTicks } from '../shared/chart.utils';

/** Consumption phases L1–L3: the expanded chart's lines and the Verbrauch card's phase bars. */
export const PHASE_COLORS: readonly string[] = ['#0d6efd', '#6610f2', '#d63384'];
/** 16 A grid limit per phase (3680 W): the dashed line in the chart and the marker on the card's phase bars. */
export const GRID_LIMIT_COLOR = 'rgba(220,53,69,0.75)';
/** 32 A battery limit (7360 W): green like the battery, so neither limit reads as a dashed orange Soll line. */
export const BATTERY_LIMIT_COLOR = 'rgba(25,135,84,0.75)';

/** Options read by the shared chart-link plugins (deepLink, scale resolution) that Chart.js' types do not declare. */
const linkOpts = (o: object): {} => o;

const DAY_MS = 24 * 3600 * 1000;
/** Phones: the breakpoint of the full-width chart cards and the full-screen popup sheets (styles.css). */
const PHONE_MQ = '(max-width: 575.98px)';

const crosshairPlugin: Plugin<'line'> = {
  id: 'crosshairLine',
  afterDraw(chart) {
    const active = (chart as any).tooltip?._active;
    if (!active?.length) return;
    const x    = active[0].element.x;
    const { ctx, chartArea } = chart;
    if (!chartArea) return;
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(x, chartArea.top);
    ctx.lineTo(x, chartArea.bottom);
    ctx.lineWidth    = 1;
    ctx.strokeStyle  = 'rgba(128,128,128,0.5)';
    ctx.setLineDash([4, 3]);
    ctx.stroke();
    ctx.restore();
  },
};
Chart.register(crosshairPlugin);

@Component({
  selector: 'app-sparkline-chart',
  template: `
    <div class="spark-box"
         [class.spark-card]="cardChart && !expanded"
         [class.spark-fill]="sheetFill && expanded"
         [style.height]="expanded ? 'auto' : '90px'"
         [style.aspect-ratio]="expanded ? '16/9' : null">
      <canvas baseChart
        [data]="chartData"
        [options]="chartOptions"
        type="line">
      </canvas>
      <button *ngIf="zoomed" class="btn btn-sm btn-outline-secondary dash-chart-reset"
              (click)="resetZoom()">{{ 'CHART_RESET' | translate }}</button>
    </div>
  `,
  styles: [`
    /* min-height 0: a 16:9 box would otherwise never shrink below its canvas (the automatic minimum of an
       aspect-ratio box is its content), e.g. after a phone sheet's tall chart turns into the desktop popup */
    .spark-box { position: relative; min-height: 0; }
    @media (max-width: 575.98px) {
      /* opt-in phone sizes (cardChart / sheetFill); !important beats the inline 90 px / 16:9 */
      .spark-card { height: var(--dash-card-chart-h) !important; }
      .spark-fill { height: 100% !important; aspect-ratio: auto !important; }
    }
  `],
})
export class SparklineChartComponent implements OnChanges, OnDestroy {
  @ViewChild(BaseChartDirective) chartDirective?: BaseChartDirective;
  @Input() points: TempDataPoint[] = [];
  @Input() darkMode = false;
  @Input() expanded = false;
  @Input() mode: 'temp' | 'humidity' = 'temp';

  @Input() solarPoints: SolarHistoryPoint[] = [];
  @Input() solarMode: 'pvYield' | 'battery' | 'grid' | 'consumption' | null = null;
  /** Visible x window in ms (now-windowMs … now). 24h → relative ticks; longer → date ticks. */
  @Input() windowMs = 24 * 3600 * 1000;
  /** Temperature number format for y ticks and tooltips (e.g. locale decimals); default one decimal with a dot. */
  @Input() numFmt: ((v: number) => string) | null = null;
  /**
   * KNX heating valve position (0..100 %) index-aligned with `points` (null = no value at that point),
   * drawn in temperature mode as a filled area on a right axis 0–100 %. Index-aligned so the shared
   * 'index' tooltip pairs it with Ist / Soll. Pass a stable array: a new reference re-renders the chart.
   */
  @Input() valve: (number | null)[] | null = null;
  /**
   * Admin only: appended to the expanded chart's Ist / Ventil dataset names (legend + tooltip), e.g. the
   * KNX group address → "Ist · 7/5/0", "Ventil · 16/5/0". Strings, so an unchanged tag re-renders nothing.
   */
  @Input() istTag: string | null = null;
  @Input() valveTag: string | null = null;
  /**
   * Deep-link id of the chart (usually the view path of the popup it sits in, e.g. 'klima/room/knx-f470'):
   * its legend selection and zoom window go into the URL. Dataset keys: temp 'ist' / 'soll' / 'valve',
   * humidity 'humidity', pvYield 'pv', battery 'power' / 'soc', grid 'grid', consumption 'total'
   * (+ 'l1' 'l2' 'l3' 'limit16a' 'limit32a' with phase data).
   */
  @Input() linkId: string | null = null;
  /**
   * Phones (below 576 px): the small chart is var(--dash-card-chart-h) tall (120 px) instead of 90 px and a
   * 24 h window is labelled "–24h" / "–12h" / "Jetzt" — for a card that spans the full phone width (styles.css
   * pattern (b)). No effect from 576 px.
   */
  @Input() cardChart = false;
  /**
   * Phones: the expanded chart fills its host's height instead of 16:9 — the host needs a height of its own,
   * e.g. a flex item filling a full-screen popup sheet (min-height!). No effect from 576 px.
   */
  @Input() sheetFill = false;
  /**
   * Phones: the expanded chart gets the compact look of the 📈 modals — legend 11 px, compact tooltip, ticks at
   * 10 px (at most 5 on y; on x every 6 h of a 24 h window, else at most 4), no axis titles. Rebuilt when the
   * width crosses 576 px. No effect from 576 px.
   */
  @Input() compactOnPhone = false;

  chartData: ChartData<'line'> = { datasets: [] };
  chartOptions: ChartConfiguration<'line'>['options'] = {};
  zoomed = false;

  /** Listens for the phone breakpoint only while cardChart / compactOnPhone is set (a resize listener on every
   *  KNX card would run change detection on each resize event). */
  private phoneMql: MediaQueryList | null = null;
  private readonly onPhoneChange = (): void => this.zone.run(() => this.build());
  /** Axis titles and dataset names are built in TS: rebuilt on a language switch. */
  private readonly langSub: Subscription;

  constructor(private translate: TranslateService, private zone: NgZone, private link: DeepLinkService) {
    this.langSub = translate.onLangChange.subscribe(() => this.build());
  }

  /** The compact phone look of the expanded chart applies (see compactOnPhone). */
  private get phone(): boolean {
    return this.compactOnPhone && this.expanded && !!this.phoneMql?.matches;
  }

  /** Phone: fixed 6 h ticks on a 24 h window (the expanded compact chart, a full-width card chart). */
  private get phoneDayTicks(): boolean {
    return this.phone || (this.cardChart && !this.expanded && !!this.phoneMql?.matches);
  }

  /** Translated label, falling back to `fallback` while the language file is still loading. */
  private label(key: string, fallback: string): string {
    const t = this.translate.instant(key);
    return t && t !== key ? t : fallback;
  }

  resetZoom(): void {
    (this.chartDirective?.chart as any)?.resetZoom();
    this.zoomed = false;
  }

  /** Zoom plugin config of the expanded chart: the Reset button follows the chart's zoom state — after a
   *  drag / pinch, a reset and a zoom window restored from the deep link. The callback runs outside Angular;
   *  it re-enters only on a change (a restore after every rebuild must not start another change detection). */
  private zoomOptions(): {} {
    return this.expanded
      ? { zoom: dragZoom(() => {
          const zoomed = !!(this.chartDirective?.chart as any)?.isZoomedOrPanned?.();
          if (zoomed !== this.zoomed) this.zone.run(() => { this.zoomed = zoomed; });
        }) }
      : {};
  }

  /** The x axis: the range's window (relative ticks for 24 h, dates beyond); zoomed in (a drag / pinch or a
   *  linked window), ticks on whole hours / days of the visible span instead. */
  private timeScale(xMin: number, xMax: number, textColor: string, gridColor: string, font: { size: number }): any {
    const phone = this.phone;
    const dayTicks = this.phoneDayTicks && this.windowMs <= DAY_MS;
    const windowTick = this.windowMs > 2 * DAY_MS ? timeAxisTick(xMin, xMax) : relativeDayTick;
    const zoomedIn = (scale: any): boolean => scale.max - scale.min < (xMax - xMin) * 0.98;
    const pad = (n: number) => String(n).padStart(2, '0');
    return {
      display: true,
      type: 'linear',
      min: xMin,
      max: xMax,
      afterBuildTicks: (scale: any) => {
        const aligned = zoomedIn(scale) ? alignedTimeTicks(scale.min, scale.max) : [];
        if (aligned.length >= 2) scale.ticks = aligned.map(value => ({ value }));
        // phone, 24 h window: a tick every 6 h, labelled "–24h" / "–12h" / "Jetzt" (the few ticks Chart.js
        // picks rarely hit –12h); only the labelled ones draw a grid line
        else if (dayTicks) scale.ticks = [24, 18, 12, 6, 0].map(h => ({ value: xMax - h * 3600_000 }));
      },
      ticks: {
        color: textColor,
        maxTicksLimit: phone ? 4 : this.expanded ? 8 : 3,
        font,
        maxRotation: 0,
        callback: function (this: any, val: number | string) {
          if (!zoomedIn(this)) return windowTick(val);
          if (this.max - this.min > 2 * DAY_MS) return timeAxisTick(this.min, this.max)(val);
          const d = new Date(val as number);
          return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
        },
      },
      // no axis title: the ticks ("–24h" / dates / "14:00") and the pressed range button say it
      grid: { display: this.expanded, color: gridColor },
    };
  }

  /** Compact legend / tooltip of the phone look (empty otherwise): the series in as few legend lines as
   *  possible, a tooltip that leaves the chart visible. */
  private legendLabels(textColor: string): {} {
    return { ...lineLegendLabels(textColor), ...(this.phone ? { padding: 6, font: { size: 11 } } : {}) };
  }
  private tooltipLook(): {} {
    return this.phone ? { bodyFont: { size: 11 }, titleFont: { size: 11 }, padding: 6, boxWidth: 8, boxHeight: 8 } : {};
  }

  /** plugins.deepLink of a linked chart. */
  private deepLinkOptions(): {} {
    return this.linkId ? linkOpts({ deepLink: { id: this.linkId } }) : {};
  }

  private fmtW(w: number): string {
    const abs = Math.abs(w);
    if (abs >= 1000) return (w / 1000).toFixed(1) + ' kW';
    return w.toFixed(0) + ' W';
  }

  ngOnChanges(_changes: SimpleChanges): void {
    const wantsPhone = this.compactOnPhone || this.cardChart;
    if (wantsPhone && !this.phoneMql && typeof window !== 'undefined' && window.matchMedia) {
      this.phoneMql = window.matchMedia(PHONE_MQ);
      this.phoneMql.addEventListener('change', this.onPhoneChange);
    } else if (!wantsPhone && this.phoneMql) {
      this.dropPhoneListener();
    }
    this.build();
  }

  ngOnDestroy(): void {
    this.langSub.unsubscribe();
    this.dropPhoneListener();
  }

  private dropPhoneListener(): void {
    this.phoneMql?.removeEventListener('change', this.onPhoneChange);
    this.phoneMql = null;
  }

  /** Data and options from the inputs (and, with cardChart / compactOnPhone, the phone breakpoint). */
  private build(): void {
    const { textColor, gridColor } = chartPalette(this.darkMode);
    const phone = this.phone;
    // New options reset the x window and with it any zoom; a linked zoom window is restored right after.
    this.zoomed = this.expanded && !!this.linkId && !!this.link.zoom(this.linkId);

    // x window spans the selected range (now-windowMs … now), so date labels show
    // across a Woche/Monat even when the archive is still sparse. 24h → relative ticks.
    const xMax = Date.now();
    const xMin = xMax - this.windowMs;

    if (this.solarMode !== null) {
      const solarColors: Record<string, string> = {
        pvYield:     '#ffc107',
        battery:     '#198754',
        grid:        '#0dcaf0',
        consumption: '#0d6efd',
      };
      const getValue = (p: SolarHistoryPoint): number => {
        switch (this.solarMode) {
          case 'pvYield':     return p.pvYieldW;
          case 'battery':     return -p.batteryDischargeW;
          case 'grid':        return p.gridConsumptionW;
          case 'consumption': return p.totalConsumptionW;
          default:            return 0;
        }
      };
      // y-axis titles: i18n key + fallback while the language file loads
      const solarYLabel: Record<string, [string, string]> = {
        pvYield:     ['SPARK_AXIS_PV',          'Erzeugung (W)'],
        battery:     ['SPARK_AXIS_BATTERY',     'Batterie (W)'],
        grid:        ['SPARK_AXIS_GRID',        'Netzbezug (W)'],
        consumption: ['SPARK_AXIS_CONSUMPTION', 'Verbrauch (W)'],
      };
      const yTitle = solarYLabel[this.solarMode] ?? solarYLabel['pvYield'];
      const miniFont = { size: this.expanded && !phone ? 11 : 10 };
      const isBattery = this.solarMode === 'battery';
      const isConsumption = this.solarMode === 'consumption';
      const hasPhases = isConsumption && this.solarPoints.some(p =>
        (p.consumptionL1W ?? 0) > 0 || (p.consumptionL2W ?? 0) > 0 || (p.consumptionL3W ?? 0) > 0);
      const showPhases = hasPhases && this.expanded;
      const showLegend = (isBattery || showPhases) && this.expanded;
      // Display precision of the W axis (ticks / tooltip via fmtW): whole watts below 1 kW, 0.1 kW from 1 kW
      // on (the phase chart's limit lines are 3.7 / 7.4 kW) — the axis' minimum span is a multiple of it.
      const wMax = this.solarPoints.reduce((m, p) => Math.max(m, Math.abs(getValue(p))), 0);
      const wResolution = showPhases || wMax >= 1000 ? 100 : 1;
      this.chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        interaction: { mode: 'index', intersect: false },
        layout: { padding: { top: 4, right: isBattery ? 0 : 6, bottom: 0, left: 0 } },
        plugins: {
          legend: showLegend
            ? { display: true, labels: this.legendLabels(textColor) }
            : { display: false },
          tooltip: {
            ...this.tooltipLook(),
            callbacks: {
              title: timeTooltipTitle,
              label: (item) => (item.dataset as any).yAxisID === 'y1'
                ? ` ${Math.round(item.parsed.y ?? 0)} %`
                : ` ${this.fmtW(item.parsed.y ?? 0)}`,
            },
          },
          ...this.zoomOptions(),
          ...this.deepLinkOptions(),
        },
        scales: {
          x: this.timeScale(xMin, xMax, textColor, gridColor, miniFont),
          y: {
            display: true,
            ...linkOpts({ resolution: wResolution }),
            ticks: {
              color: textColor,
              maxTicksLimit: phone ? 5 : this.expanded ? 6 : 4,
              font: miniFont,
              callback: (val) => this.fmtW(val as number),
            },
            grid: { color: gridColor, display: this.expanded },
            title: {
              display: this.expanded && !phone,
              text: this.label(yTitle[0], yTitle[1]),
              color: textColor,
              font: miniFont,
            },
          },
          ...(isBattery ? {
            y1: {
              position: 'right' as const,
              min: 0,
              max: 100,
              display: true,
              ticks: {
                color: textColor,
                font: miniFont,
                maxTicksLimit: phone ? 5 : this.expanded ? 6 : 4,
                callback: (val: any) => `${val} %`,
              },
              grid: { drawOnChartArea: false },
            },
          } : {}),
        },
        elements: {
          point: { radius: 0, hoverRadius: 4 },
          line:  { borderWidth: this.expanded ? 2 : 1.5, tension: 0.4 },
        },
      };
      if (showPhases) {
        const phaseLabels = ['L1', 'L2', 'L3'];
        const phaseGetters = [
          (p: SolarHistoryPoint) => p.consumptionL1W ?? 0,
          (p: SolarHistoryPoint) => p.consumptionL2W ?? 0,
          (p: SolarHistoryPoint) => p.consumptionL3W ?? 0,
        ];
        const datasets: any[] = phaseGetters.map((fn, i) => ({
          label: phaseLabels[i],
          linkKey: phaseLabels[i].toLowerCase(),
          data: this.solarPoints.map(p => ({ x: p.ts, y: fn(p) })),
          borderColor: PHASE_COLORS[i],
          backgroundColor: 'transparent',
          fill: false,
          borderWidth: 2,
        }));
        datasets.push({
          label: this.label('SPARK_PHASES_TOTAL', 'Gesamt'),
          linkKey: 'total',
          data: this.solarPoints.map(p => ({ x: p.ts, y: p.totalConsumptionW })),
          borderColor: 'rgba(108,117,125,0.5)',
          backgroundColor: 'transparent',
          fill: false,
          borderWidth: 1,
          borderDash: [4, 3],
        });
        // Grid barrier line (16A = 3680W), across the whole window
        datasets.push({
          label: this.label('SPARK_LIMIT_GRID', '16 A Netz (3680 W)'),
          linkKey: 'limit16a',
          data: [{ x: xMin, y: 3680 }, { x: xMax, y: 3680 }],
          borderColor: GRID_LIMIT_COLOR,
          backgroundColor: 'transparent',
          fill: false,
          borderWidth: 1.5,
          borderDash: [6, 4],
          pointRadius: 0,
          pointHoverRadius: 0,
        });
        // Battery barrier line (32A = 7360W)
        datasets.push({
          label: this.label('SPARK_LIMIT_BATTERY', '32 A Akku (7360 W)'),
          linkKey: 'limit32a',
          data: [{ x: xMin, y: 7360 }, { x: xMax, y: 7360 }],
          borderColor: BATTERY_LIMIT_COLOR,
          backgroundColor: 'transparent',
          fill: false,
          borderWidth: 1.5,
          borderDash: [6, 4],
          pointRadius: 0,
          pointHoverRadius: 0,
        });

        this.chartData = { datasets };
        return;
      }

      const solarKeys: Record<string, string> = { pvYield: 'pv', battery: 'power', grid: 'grid', consumption: 'total' };
      const datasets: any[] = [{
        label: isBattery ? 'W' : undefined,
        linkKey: solarKeys[this.solarMode] ?? 'value',
        data: this.solarPoints.map(p => ({ x: p.ts, y: getValue(p) })),
        borderColor: solarColors[this.solarMode] ?? '#6c757d',
        backgroundColor: 'transparent',
        fill: false,
      }];
      if (isBattery) {
        datasets.push({
          label: '%',
          linkKey: 'soc',
          data: this.solarPoints.map(p => ({ x: p.ts, y: p.batteryPercent })),
          borderColor: '#a855f7',
          backgroundColor: 'transparent',
          fill: false,
          yAxisID: 'y1',
          borderDash: [4, 3],
        });
      }
      this.chartData = { datasets };
      return;
    }

    const miniFont = { size: this.expanded && !phone ? 11 : 10 };
    // KNX rooms carry the heating target (Soll) next to the actual temperature (Ist).
    const hasSetpoint = this.mode === 'temp' && this.points.some(p => typeof p.setpointC === 'number');
    // …and, with a mapped heating valve, its position (temperature mode only: it explains Ist vs Soll).
    const valve = this.mode === 'temp' && this.valve?.length === this.points.length && this.valve.some(v => v != null)
      ? this.valve : null;
    const valveColor = SparklineChartComponent.VALVE_COLOR;
    const fmtTemp = this.numFmt ?? ((v: number) => v.toFixed(1));
    const valveSep = this.expanded && this.valveTag ? ':' : '';
    this.chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      interaction: { mode: 'index', intersect: false },
      layout: { padding: { top: 4, right: 6, bottom: 0, left: 0 } },
      plugins: {
        legend: (hasSetpoint || valve) && this.expanded
          ? { display: true, labels: this.legendLabels(textColor) }
          : { display: false },
        tooltip: {
          ...this.tooltipLook(),
          callbacks: {
            title: timeTooltipTitle,
            label: (item) => (item.dataset as any).yAxisID === 'y1'
              ? ` ${item.dataset.label}${valveSep} ${Math.round(item.parsed.y ?? 0)} %`   // "Ventil 45 %" (admin: "Ventil · 16/5/0: 45 %")
              : this.mode === 'humidity'
                ? ` ${(item.parsed.y ?? 0).toFixed(0)} %`
                : hasSetpoint || valve
                  ? ` ${item.dataset.label}: ${fmtTemp(item.parsed.y ?? 0)} °C`
                  : ` ${fmtTemp(item.parsed.y ?? 0)} °C`,
            // Ist/Soll/Ventil swatches in the line colours, so the values are told apart at a glance.
            ...(hasSetpoint || valve ? {
              labelColor: (item: any) => ({ borderColor: item.dataset.borderColor, backgroundColor: item.dataset.borderColor }),
            } : {}),
          },
        },
        ...this.zoomOptions(),
        ...this.deepLinkOptions(),
      },
      scales: {
        x: this.timeScale(xMin, xMax, textColor, gridColor, miniFont),
        y: {
          display: true,
          // The precision the ticks / tooltip show (°C one decimal, % whole): the minimum span of the
          // axis is a multiple of it (or of the data's own step, e.g. 1 °C), so noise stays flat.
          ...linkOpts({ resolution: this.mode === 'humidity' ? 1 : 0.1 }),
          // The flat Soll line usually sits on the min/max: keep it off the chart edge.
          ...(hasSetpoint ? { grace: '5%' } : {}),
          ticks: {
            color: textColor,
            maxTicksLimit: phone ? 5 : this.expanded ? 6 : 4,
            font: miniFont,
            callback: (val) => this.mode === 'humidity'
              ? `${(val as number).toFixed(0)} %`
              : `${fmtTemp(val as number)} °`,
          },
          grid: { color: gridColor, display: this.expanded },
          title: {
            display: this.expanded && !phone,
            text: this.mode === 'humidity'
              ? this.label('SPARK_AXIS_HUMIDITY', 'Luftfeuchtigkeit (%)')
              : this.label('SPARK_AXIS_TEMP', 'Temperatur (°C)'),
            color: textColor,
            font: miniFont,
          },
        },
        // Valve position: fixed 0–100 % on the right (like the buffer charge); the axis itself only in
        // the expanded chart — on the small card the area alone reads as "how far open".
        ...(valve ? {
          y1: {
            position: 'right' as const,
            min: 0,
            max: 100,
            display: this.expanded,
            ticks: { color: textColor, font: miniFont, stepSize: 25, maxTicksLimit: 5, callback: (val: any) => `${val} %` },
            grid: { drawOnChartArea: false },
            title: { display: this.expanded && !phone, text: this.label('KNX_VALVE_AXIS', 'Ventil (%)'), color: textColor, font: miniFont },
          },
        } : {}),
      },
      elements: {
        point: { radius: 0, hoverRadius: 4 },
        line:  { borderWidth: this.expanded ? 2 : 1.5, tension: 0.6 },
      },
    };

    const isHumidity = this.mode === 'humidity';
    // Admin tags (group addresses) only where the legend shows the names
    const tagged = (name: string, tag: string | null): string => this.expanded && tag ? `${name} · ${tag}` : name;
    const datasets: any[] = [{
      // Named whenever the tooltip / legend name the datasets (Soll or the valve beside it)
      label: hasSetpoint || valve ? tagged(this.label('TEMP_ACTUAL', 'Ist'), this.istTag) : undefined,
      linkKey: isHumidity ? 'humidity' : 'ist',
      data: this.points
        .filter(p => !isHumidity || p.humidity != null)
        .map(p => ({ x: p.ts, y: isHumidity ? (p.humidity as number) : p.tempC })),
      borderColor: isHumidity ? '#0dcaf0' : '#0d6efd',
      backgroundColor: 'transparent',
      fill: false,
    }];
    if (hasSetpoint) {
      // Set-point changes are discrete steps, so no smoothing; dashed in the dashboard's Soll colour.
      // Index-aligned with the Ist data (null where missing) so the 'index' tooltip pairs them.
      datasets.push({
        label: this.label('TEMP_SETPOINT', 'Soll'),
        linkKey: 'soll',
        data: this.points.map(p => ({ x: p.ts, y: typeof p.setpointC === 'number' ? p.setpointC : null })),
        borderColor: setpointColor(this.darkMode),
        backgroundColor: 'transparent',
        fill: false,
        spanGaps: true,
        stepped: 'before',
        tension: 0,
        borderDash: [5, 4],
        borderWidth: this.expanded ? 2 : 1.5,
        pointRadius: 0,
        pointHoverRadius: 0,
      });
    }
    if (valve) {
      // Valve position as a faint filled area behind the temperature lines (higher order = drawn first).
      // The archive holds sampled values (sent cyclically / on change): held until the next one, no smoothing.
      datasets.push({
        label: tagged(this.label('KNX_VALVE', 'Ventil'), this.valveTag),
        linkKey: 'valve',
        data: this.points.map((p, i) => ({ x: p.ts, y: valve[i] })),
        yAxisID: 'y1',
        borderColor: valveColor,
        backgroundColor: this.darkMode ? 'rgba(12,166,120,0.15)' : 'rgba(12,166,120,0.12)',
        fill: 'origin',
        stepped: 'before',
        tension: 0,
        spanGaps: false,
        borderWidth: this.expanded ? 1.5 : 1,
        pointRadius: 0,
        pointHoverRadius: 3,
        order: 1,
      });
    }
    this.chartData = { datasets };
  }

  /** Valve area colour (teal): checked for colour-vision-deficiency separation against the blue Ist and
   *  the orange Soll line on the light and the dark card surface. */
  static readonly VALVE_COLOR = '#0ca678';
}
